/**
 * BasemapSwitcher — Map · Streets · Satellite · Terrain, floating on a geographic map.
 *
 * A segmented control rather than a dropdown: there are only four styles, and an operator
 * deciding whether imagery would help should see that it exists without opening anything.
 * The page places it; the choice itself lives in `useBasemap`.
 */
import { BASEMAP_IDS, BASEMAP_LABELS, type BasemapId } from './basemaps';

export interface BasemapSwitcherProps {
  value: BasemapId;
  onChange: (id: BasemapId) => void;
  className?: string;
}

export default function BasemapSwitcher({ value, onChange, className = '' }: BasemapSwitcherProps) {
  return (
    <div
      role="group"
      aria-label="Map style"
      // Solid `bg-card`, not `bg-card/90`: the theme colours are bare `var()`s, so an opacity
      // modifier on them compiles to nothing and the control would float with no background.
      className={`flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5 shadow-card ${className}`}
    >
      {BASEMAP_IDS.map((id) => {
        const active = id === value;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(id)}
            className={`h-6 rounded px-2 text-[11px] font-medium transition-colors ${
              active ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {BASEMAP_LABELS[id]}
          </button>
        );
      })}
    </div>
  );
}
